/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { JobDto } from '@/commons/dtos';
import { useTranslations } from '@/hooks/useTranslations';
import '@ui5/webcomponents-icons/dist/in-progress-2.js';
import { Icon } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useMemo } from 'react';
import Tooltip from './Tooltip';

export type IntegrationStatus = 'in-progress' | 'success' | 'failed';

export default function IntegrationStatusIcon({ status }: { status: IntegrationStatus }) {
    const { t } = useTranslations();
    const { icon, color, popoverContent } = useMemo(
        () =>
            ({
                success: {
                    icon: 'sys-enter-2',
                    color: ThemingParameters.sapSuccessColor,
                    popoverContent: t('INTEGRATION_STATUS_ICON_TOOLTIP_SUCCESS_CONTENT'),
                },
                'in-progress': {
                    icon: 'in-progress-2',
                    color: ThemingParameters.sapInformationColor,
                    popoverContent: t('INTEGRATION_STATUS_ICON_TOOLTIP_IN_PROGRESS_CONTENT'),
                },
                failed: {
                    icon: 'sys-cancel-2',
                    color: ThemingParameters.sapErrorColor,
                    popoverContent: t('INTEGRATION_STATUS_ICON_TOOLTIP_FAILED_CONTENT'),
                },
            })[status],
        [status]
    );

    return (
        <Tooltip content={popoverContent}>
            <Icon name={icon} style={{ color }} />
        </Tooltip>
    );
}

export function mapJobStatusToIntegrationStatus(jobStatus: JobDto['status']): IntegrationStatus {
    switch (jobStatus) {
        case 'FAILED':
            return 'failed';
        case 'RUNNING':
            return 'in-progress';
        case 'COMPLETED':
            return 'success';
        default:
            console.error(`Job status ${jobStatus} is not supported`);
            return 'in-progress';
    }
}
